import React from "react";
import { GSCProperty } from "../types/gsc.types";

interface PropertyCardProps {
  property: GSCProperty;
  onConnect: (property: GSCProperty) => void;
  isConnected: boolean;
  isSaving: boolean;
}

/** Google's permissionLevel values, as something a person would recognise. */
const PERMISSION_LABELS: Record<string, string> = {
  siteOwner: "Owner",
  siteFullUser: "Full access",
  siteRestrictedUser: "Restricted",
};

/**
 * A domain property covers every subdomain and protocol; a URL-prefix property covers
 * only the exact prefix. Worth saying, because a customer commonly sees both listed for
 * what they think of as one site.
 */
function describeProperty(siteUrl: string): { name: string; kind: string } {
  return siteUrl.startsWith("sc-domain:")
    ? { name: siteUrl.slice("sc-domain:".length), kind: "Domain property" }
    : { name: siteUrl, kind: "URL prefix property" };
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onConnect,
  isConnected,
  isSaving,
}) => {
  const { name, kind } = describeProperty(property.site_url);
  const permission = property.permission_level
    ? PERMISSION_LABELS[property.permission_level] ?? property.permission_level
    : undefined;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-brand-primary transition-colors shadow-sm">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900 break-all">{name}</h3>
        <p className="text-sm text-gray-600 mt-0.5">{kind}</p>
        {permission && (
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-green-100 text-green-800 rounded">
              {permission}
            </span>
          </div>
        )}
      </div>
      <div className="mt-auto">
        {isConnected ? (
          <p className="text-sm font-medium text-green-700 py-2">Connected</p>
        ) : (
          <button
            type="button"
            onClick={() => onConnect(property)}
            disabled={isSaving}
            className="w-full py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-1">
                <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                Connecting...
              </span>
            ) : (
              "Connect"
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
